/**
 * Geometria do card enquanto os números não chegaram.
 *
 * ★ A regra que rege este arquivo: o esqueleto NÃO inventa dado. O rótulo, a
 * régua da escala e a quantidade de marcas do micro-indicador são ESTRUTURA —
 * vêm do catálogo e podem ser desenhados de verdade. Já o valor, a legenda e a
 * posição do marcador são MEDIÇÃO — viram caixa neutra, porque um trilho
 * preenchido pela metade ou uma linha reta afirmariam algo que ninguém mediu
 * ainda.
 *
 * Mora fora do `.vue` porque é geometria pura: dá para testar sem montar o
 * componente, e o card já passa de 480 linhas.
 */

// Caixas do `BaseLoadingSkeleton`, calibradas na tipografia real de cada slot
// (`_ia-insight-card` styles: valor 17px, hero 26px, aux/contador 12px).
// Larguras aproximam o texto típico — esqueleto do tamanho errado
// desloca o layout quando o dado chega.
export const CAIXA = {
  valor: { width: '96px', height: '20px' },
  valorHero: { width: '136px', height: '30px' },
  aux: { width: '70%', height: '14px' },
  contador: { width: '64px', height: '14px' },
  // [feedback do usuário, 06/09] "ta muito para baixo e falta coisa" — o
  // `aux` de cima (70%) SOME (mede 0px de largura, verificado no DOM) nos
  // dois slots do `serie`, porque ali o `<span class="ia-insight__aux">` é
  // item de FLEX em linha sem largura própria (shrink-to-fit): uma
  // porcentagem dentro de um container sem largura definida resolve como
  // "auto"/0, não como fração de algo. Largura em PX — não em `%` — para não
  // depender dessa resolução circular.
  auxSerieDestaque: { width: '52px', height: '14px' },
  auxSerieLegenda: { width: '150px', height: '14px' },
}

// Cinza do próprio esqueleto — as marcas pendentes não carregam TOM, porque tom
// é julgamento sobre o número ("isto é um alerta") e ainda não há número.
//
// `--neutral-300` e não `200`: sobre a superfície quente da lateral o 200 some,
// e as marcas viravam manchas claras. Mesma medição do trilho da escala.
const NEUTRO_PENDENTE = 'var(--neutral-300)'

// Perfil canônico das barrinhas — a MESMA ideia da onda do sparkline fantasma.
// Altura uniforme foi a primeira tentativa e estava errada: a fileira reta lia
// como cinco quadradinhos, enquanto o card carregado mostra um mini-gráfico.
// Como o cinza não carrega tom, a forma pode espelhar sem afirmar tendência —
// exatamente o critério já aceito para a curva do sparkline.
const PERFIL_BARRAS_FANTASMA = [45, 70, 100, 80, 55]

const listaDe = (quantidade) => {
  const total = Math.max(0, Number(quantidade) || 0)
  return Array.from({ length: total }, (_, indice) => indice)
}

/**
 * Régua da escala sem nenhuma banda marcada como ativa.
 * @param {Object} esqueleto bloco `esqueleto` do catálogo
 * @returns {Array<{rotulo: string, ativa: boolean}>}
 */
export const bandasPendentes = (esqueleto) => {
  const bandas = (esqueleto || {}).bandas || []
  return bandas.map((rotulo) => ({ rotulo, ativa: false }))
}

/**
 * Pontos do card compacto, na quantidade que o catálogo declara.
 * @param {Object} esqueleto bloco `esqueleto` do catálogo
 * @returns {Array<{chave: number, estilo: Object}>}
 */
export const pontosPendentes = (esqueleto) => {
  const quantidade = (esqueleto || {}).pontos
  if (!quantidade) return []
  return listaDe(quantidade).map((indice) => ({
    chave: indice,
    estilo: { background: NEUTRO_PENDENTE },
  }))
}

/**
 * Barrinhas do card compacto, todas na mesma altura.
 * @param {Object} esqueleto bloco `esqueleto` do catálogo
 * @returns {Array<{chave: number, estilo: Object}>}
 */
export const barrasPendentes = (esqueleto) => {
  const quantidade = (esqueleto || {}).barras
  if (!quantidade) return []
  return listaDe(quantidade).map((indice) => ({
    chave: indice,
    estilo: {
      // O perfil se repete se o catálogo pedir mais barras que ele tem.
      height:
        PERFIL_BARRAS_FANTASMA[indice % PERFIL_BARRAS_FANTASMA.length] + '%',
      background: NEUTRO_PENDENTE,
    },
  }))
}

/**
 * Trilho de faixas sem divisão: uma faixa neutra ocupando tudo. O `hero` e os
 * compactos com `segmentos` não declaram quantidade porque a divisão É o dado —
 * mostrar três fatias antes de saber a proporção seria inventar a proporção.
 * @returns {Array<{chave: number, estilo: Object}>}
 */
export const faixasPendentes = () => [
  { chave: 0, estilo: { flex: 1, background: NEUTRO_PENDENTE } },
]
